import { navigationTargetHandle } from 'utils/navigation-target-handle';

const DRAWER_MENU_BLOCKS_INJECTED = 'data-drawer-menu-blocks-injected';

const normalizeHref = (href: string | null | undefined) => {
  if (!href) return '';

  try {
    const url = new URL(href, window.location.origin);
    return url.pathname.replace(/\/$/, '') || '/';
  } catch {
    return href.split('?')[0]?.replace(/\/$/, '') ?? '';
  }
};

const getLabel = (link: Element) => {
  const label = link.querySelector<HTMLElement>('[data-nav-link-label]')?.textContent;
  if (label?.trim()) return label.trim();

  return link.textContent?.trim() ?? '';
};

const collectSublistHrefs = (sublist: HTMLUListElement) => {
  const hrefs = new Set<string>();

  sublist
    .querySelectorAll<HTMLAnchorElement>(':scope > li a.drawer__nav-row[href]')
    .forEach((link) => {
      const normalized = normalizeHref(link.getAttribute('href'));
      if (normalized) hrefs.add(normalized);
    });

  return hrefs;
};

const getDrawerItemHref = (item: HTMLLIElement) =>
  item.querySelector<HTMLAnchorElement>('a.drawer__nav-row[href]')?.getAttribute('href') ?? null;

const resolveDesktopRootHref = (header: HTMLElement, target: string) => {
  for (const rootLi of header.querySelectorAll<HTMLElement>('.header__menu-root-item')) {
    const link = rootLi.querySelector<HTMLAnchorElement>(':scope > a');
    if (!link) continue;

    if (navigationTargetHandle(getLabel(link)) === target) {
      return link.getAttribute('href');
    }
  }

  return null;
};

const ensureDrawerSublist = (link: HTMLAnchorElement, li: HTMLLIElement) => {
  const existingPanel = link.nextElementSibling;
  if (existingPanel instanceof HTMLElement && existingPanel.hasAttribute('data-list')) {
    return existingPanel.querySelector<HTMLUListElement>('ul.drawer__nav-sublist');
  }

  link.setAttribute('aria-haspopup', 'true');
  link.setAttribute('aria-expanded', 'false');

  if (!link.querySelector('.ms-auto.size-5')) {
    const chevron = document.createElement('span');
    chevron.className = 'ms-auto size-5 shrink-0';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.innerHTML =
      '<svg class="size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" /></svg>';
    link.appendChild(chevron);
  }

  const panel = document.createElement('div');
  panel.className = 'drawer-navigation__list';
  panel.setAttribute('data-list', '');
  panel.hidden = true;

  const sublist = document.createElement('ul');
  sublist.className = 'drawer__nav-sublist';
  sublist.setAttribute('role', 'menu');
  sublist.setAttribute('aria-label', getLabel(link));

  panel.appendChild(sublist);
  li.appendChild(panel);

  return sublist;
};

const findDrawerSublistByHref = (navRoot: HTMLElement, href: string) => {
  const normalized = normalizeHref(href);
  if (!normalized) return null;

  for (const link of navRoot.querySelectorAll<HTMLAnchorElement>('a.drawer__nav-row[href]')) {
    if (normalizeHref(link.getAttribute('href')) !== normalized) continue;

    const li = link.closest<HTMLLIElement>('li.drawer__nav-item');
    if (!li) continue;

    const subListPanel = link.nextElementSibling;
    if (subListPanel instanceof HTMLElement && subListPanel.hasAttribute('data-list')) {
      const sublist = subListPanel.querySelector<HTMLUListElement>('ul.drawer__nav-sublist');
      if (sublist) return sublist;
    }

    return ensureDrawerSublist(link, li);
  }

  return null;
};

const findDrawerSublistByTitle = (navRoot: HTMLElement, target: string) => {
  const rootList = navRoot.querySelector<HTMLUListElement>(
    'ul.drawer__nav-list:not(.drawer__nav-list--other)',
  );
  if (!rootList) return null;

  for (const link of rootList.querySelectorAll<HTMLElement>(
    ':scope > li > a[aria-haspopup="true"]',
  )) {
    if (navigationTargetHandle(getLabel(link)) !== target) continue;

    const subListPanel = link.nextElementSibling;
    if (!(subListPanel instanceof HTMLElement)) continue;

    return subListPanel.querySelector<HTMLUListElement>('ul.drawer__nav-sublist');
  }

  return null;
};

const findDrawerSublist = (header: HTMLElement, navRoot: HTMLElement, target: string) => {
  const desktopHref = resolveDesktopRootHref(header, target);
  if (desktopHref) {
    const byHref = findDrawerSublistByHref(navRoot, desktopHref);
    if (byHref) return byHref;
  }

  return findDrawerSublistByTitle(navRoot, target);
};

/**
 * Mega menu cards live in header templates; inject drawer nav rows into matching parent sublists.
 */
export function initHeaderDrawerMenuBlocks(header: HTMLElement) {
  const drawer = document.querySelector<HTMLElement>('[data-load-target="header-drawer"]');
  const navRoot = drawer?.querySelector<HTMLElement>('#header-drawer-nav-root');
  if (!navRoot) return;

  header.querySelectorAll<HTMLTemplateElement>('template[data-target]').forEach((template) => {
    const target = template.dataset.target?.trim();
    if (!target) return;

    const sublist = findDrawerSublist(header, navRoot, target);
    if (!sublist || sublist.hasAttribute(DRAWER_MENU_BLOCKS_INJECTED)) return;

    const existingHrefs = collectSublistHrefs(sublist);
    let injected = false;

    template.content
      .querySelectorAll<HTMLElement>('[data-header-drawer-nav-item]')
      .forEach((wrapper) => {
        const item = wrapper.firstElementChild;
        if (!(item instanceof HTMLLIElement)) return;

        const itemHref = normalizeHref(getDrawerItemHref(item));
        if (itemHref && existingHrefs.has(itemHref)) return;

        const rowIndex = sublist.querySelectorAll(':scope > li').length;
        const clone = document.importNode(item, true) as HTMLLIElement;
        clone.style.setProperty('--delay', `${40 * rowIndex}ms`);
        sublist.appendChild(clone);
        injected = true;

        if (itemHref) {
          existingHrefs.add(itemHref);
        }
      });

    if (injected) {
      sublist.setAttribute(DRAWER_MENU_BLOCKS_INJECTED, '');
    }
  });
}
